import { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';

import { ApiError } from '@mds/shared/lib/api';
import type { AdminBooking } from '@mds/shared/types/booking';
import type { AdminResource, ResourceWeekday } from '@mds/shared/types/resource';
import type { DashboardDrilldownMetric } from '@mds/shared/types/reporting';

import { useSession } from '@/auth/use-session';
import * as bookings from '@/bookings/bookings.api';
import BookingDetails from '@/components/booking/booking-details';
import DrilldownModal from '@/components/reporting/drilldown-modal';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Modal from '@/components/ui/modal';
import StatCard from '@/components/ui/stat-card';
import {
  bookingStatusLabel,
  bookingStatusTone,
  bookingSpace,
  bookingTime,
} from '@/domain/bookings';
import { eventDateLabel, eventWhen, monthKeyOf } from '@/domain/events';
import { hourMarks, isOpenAt, weekdayOf } from '@/domain/operating-hours';
import * as events from '@/events/events.api';
import { clockRange, hourShort, keyOf } from '@mds/shared/lib/dates';
import { formatCents } from '@/lib/money';
import * as reporting from '@/reporting/reporting.api';
import * as resources from '@/resources/resources.api';

/** One day's bookings fit on a page; anything past this is not totalled here. */
const PAGE_SIZE = 100;

/** The strip shows the next few; the rest of the month is on the Events page. */
const UPCOMING = 4;

/** 5000 → "50%". The server counts in basis points; the tile needs no more than a decimal. */
const occupancyLabel = (basisPoints: number) =>
  `${(basisPoints / 100).toFixed(basisPoints % 100 === 0 ? 0 : 1)}%`;

/** Front-desk landing page: today's figures, the hour grid, and what is still to come. */
export default function DashboardPage() {
  const navigate = useNavigate();
  const { can } = useSession();
  const mayRead = can('reservations.read');
  const maySeeEvents = can('events.read');
  const maySeeReports = can('reports.read');

  const [open, setOpen] = useState<AdminBooking | null>(null);
  const [drilldown, setDrilldown] = useState<DashboardDrilldownMetric | null>(null);
  const today = useMemo(() => keyOf(new Date()), []);
  const nowMinute = useMemo(() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  }, []);

  // The thirteen figures behind the tiles, counted by the server over the whole
  // day rather than by this page over one page of bookings.
  const summary = useQuery({
    queryKey: ['reports', 'dashboard', today],
    queryFn: () => reporting.getDashboard(today),
    enabled: maySeeReports,
  });

  const list = useQuery({
    queryKey: ['bookings', { date: today, limit: PAGE_SIZE }],
    queryFn: () =>
      bookings.listBookings({
        date: today,
        limit: PAGE_SIZE,
        sort: 'bookingDate',
        direction: 'asc',
      }),
    enabled: mayRead,
  });

  const inventory = useQuery({
    queryKey: ['resources', { includeInactive: false }],
    queryFn: () => resources.listResources(),
  });

  // The list endpoint filters by whole months, and what is "still to come"
  // spills over the end of one — so this month and the next are read together
  // and the cut at today is made here.
  const programme = useQuery({
    queryKey: ['events', 'upcoming', today],
    queryFn: async () => {
      const now = new Date();
      const pages = await Promise.all(
        [0, 1].map((ahead) =>
          events.listEvents({
            month: monthKeyOf(new Date(now.getFullYear(), now.getMonth() + ahead, 1)),
            limit: PAGE_SIZE,
            sort: 'eventDate',
            direction: 'asc',
          }),
        ),
      );
      return pages.flatMap((page) => page.data);
    },
    enabled: maySeeEvents,
  });

  /** `yyyy-mm-dd` sorts as it reads, so today's events are still to come. */
  const ahead = useMemo(
    () => (programme.data ?? []).filter((e) => e.eventDate >= today),
    [programme.data, today],
  );
  const upcoming = ahead.slice(0, UPCOMING);

  const rows = useMemo(() => list.data?.data ?? [], [list.data]);
  const live = useMemo(() => rows.filter((b) => b.lifecycleStatus !== 'cancelled'), [rows]);

  /** Only the rooms carry a time, so only they belong on an hour grid. */
  const timedSpaces: AdminResource[] = useMemo(
    () => (inventory.data ?? []).filter((r) => r.kind === 'room'),
    [inventory.data],
  );

  // The grid covers whatever the rooms are open for today. Operating hours are
  // editable, so the span is read off the resources rather than fixed here.
  const weekday = useMemo(() => weekdayOf(new Date()), []);
  const hours = useMemo(() => hourMarks(timedSpaces, weekday), [timedSpaces, weekday]);

  const stillToCome = useMemo(
    () => live.filter((b) => b.startMinute === null || (b.endMinute ?? 0) > nowMinute),
    [live, nowMinute],
  );

  /**
   * The tiles read one server-counted figure each.
   *
   * They were summed in the browser over a single page of today's bookings
   * until `/admin/reports/dashboard` existed, which is why they carried a
   * "Provisional" tag. The tag is gone because the number is now the real one.
   */
  const figures = summary.data;
  const tile = (render: (d: NonNullable<typeof figures>) => string, fallback = '—') =>
    !maySeeReports ? '—' : figures ? render(figures) : fallback;
  const denied = 'Not available to your account';

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard
          label="Bookings today"
          value={tile((d) => String(d.bookings.total))}
          note={
            maySeeReports
              ? figures
                ? `${figures.bookings.desk} desks · ${figures.bookings.room} rooms`
                : 'Counting…'
              : denied
          }
          density="compact"
          size="lg"
          onClick={maySeeReports ? () => setDrilldown('bookings') : undefined}
          actionLabel="Show the bookings behind this figure"
        />
        <StatCard
          label="Taken today"
          value={tile((d) => formatCents(d.payments.collectedAmountMinor))}
          note={
            maySeeReports
              ? figures
                ? `${figures.payments.paymentCount} ${
                    figures.payments.paymentCount === 1 ? 'payment' : 'payments'
                  } recorded`
                : 'Counting…'
              : denied
          }
          density="compact"
          size="lg"
          onClick={maySeeReports ? () => setDrilldown('collected') : undefined}
          actionLabel="Show the payments behind this figure"
        />
        <StatCard
          label="Still unpaid"
          value={tile((d) => formatCents(d.payments.outstandingAmountMinor))}
          note={
            maySeeReports
              ? figures
                ? `${figures.payments.outstandingBookingCount} to collect`
                : 'Counting…'
              : denied
          }
          density="compact"
          size="lg"
          onClick={maySeeReports ? () => setDrilldown('outstanding') : undefined}
          actionLabel="Show the unpaid bookings behind this figure"
        />
        <StatCard
          label="Expenses today"
          value={tile((d) => formatCents(d.expenses.totalAmountMinor))}
          note={
            maySeeReports
              ? figures
                ? `${figures.expenses.expenseCount} recorded · ${formatCents(figures.expenses.netAmountMinor)} net`
                : 'Counting…'
              : denied
          }
          density="compact"
          size="lg"
          onClick={can('expenses.read') ? () => navigate('/expenses') : undefined}
          actionLabel="Open expenses"
        />
        <StatCard
          label="Desks taken"
          value={tile((d) => `${d.desks.occupiedCount}/${d.desks.activeCount}`)}
          note={
            maySeeReports
              ? figures
                ? `${occupancyLabel(figures.desks.occupancyBasisPoints)} of active desks`
                : 'Counting…'
              : denied
          }
          density="compact"
          size="lg"
          onClick={maySeeReports ? () => setDrilldown('occupied_desks') : undefined}
          actionLabel="Show the desks behind this figure"
        />
        {/* Seats awaiting payment are called out because they do not hold a
            place: capacity is only consumed once the money is recorded. */}
        <StatCard
          label="Event registrations"
          value={tile((d) => String(d.events.confirmedSeatCount))}
          note={
            maySeeReports
              ? figures
                ? `Across ${figures.events.upcomingEventCount} ${
                    figures.events.upcomingEventCount === 1 ? 'event' : 'events'
                  } · ${figures.events.paymentPendingSeatCount} awaiting payment`
                : 'Counting…'
              : denied
          }
          density="compact"
          size="lg"
          onClick={maySeeReports ? () => setDrilldown('upcoming_events') : undefined}
          actionLabel="Show the upcoming events behind this figure"
        />
      </div>

      {summary.isError && (
        <p className="m-0 text-base text-warn-text">
          {summary.error instanceof ApiError
            ? summary.error.message
            : "Today's figures could not be read."}
        </p>
      )}

      <Card
        title="Today at a glance"
        action={<Button onClick={() => navigate('/spaces')}>+ New booking</Button>}
      >
        {!mayRead ? (
          <EmptyState
            title="Bookings are not available to your account"
            hint="Ask a super administrator for permission to read reservations."
          />
        ) : list.isPending || inventory.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading today's bookings…</p>
        ) : list.isError ? (
          <EmptyState
            title="Could not load today's bookings"
            hint={
              list.error instanceof ApiError
                ? list.error.message
                : 'The server could not be reached.'
            }
          />
        ) : timedSpaces.length === 0 ? (
          <EmptyState
            title="No rooms in the inventory"
            hint="The grid plots rooms by the hour. Add one from Spaces."
          />
        ) : hours.length === 0 ? (
          <EmptyState
            title="Every room is closed today"
            hint="Opening hours are set per room on Spaces."
          />
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[600px]">
              <div
                className="grid"
                style={{ gridTemplateColumns: `56px repeat(${timedSpaces.length}, minmax(0,1fr))` }}
              >
                <span />
                {timedSpaces.map((s) => (
                  <span
                    key={s.id}
                    className="pb-2 text-center font-mono text-sm leading-tight tracking-label text-ink-muted uppercase"
                  >
                    {s.name}
                  </span>
                ))}
                {hours.map((h) => (
                  <HourRow
                    key={h}
                    hour={h}
                    weekday={weekday}
                    spaces={timedSpaces}
                    bookings={live}
                    onPick={setOpen}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_1fr]">
        <Card title="Still to come today">
          {!mayRead || stillToCome.length === 0 ? (
            <p className="m-0 text-base text-ink-muted">
              {mayRead ? 'Nothing left on the board today.' : 'Not available to your account.'}
            </p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {stillToCome.map((b) => (
                <li
                  key={b.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-hairline px-4 py-3"
                >
                  <div className="flex flex-col gap-0.5">
                    <span className="text-base font-semibold">{b.customer.name}</span>
                    <span className="text-base text-ink-muted">{bookingSpace(b)}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-md text-ink-secondary">{bookingTime(b)}</span>
                    <Chip className={'border ' + bookingStatusTone(b.status)}>
                      {bookingStatusLabel(b.status)}
                    </Chip>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card
          title="Upcoming events"
          action={
            <Link
              to="/events"
              className="inline-flex min-h-(--target-control) items-center font-mono text-base font-semibold tracking-wide text-brand-text uppercase underline decoration-2 underline-offset-4 hover:no-underline focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)"
            >
              All events →
            </Link>
          }
        >
          {!maySeeEvents ? (
            <p className="m-0 text-base text-ink-muted">Not available to your account.</p>
          ) : programme.isPending ? (
            <p className="m-0 text-base text-ink-muted">Loading the programme…</p>
          ) : programme.isError ? (
            <p className="m-0 text-base text-ink-muted">
              {programme.error instanceof ApiError
                ? programme.error.message
                : 'The programme could not be read.'}
            </p>
          ) : upcoming.length === 0 ? (
            <p className="m-0 text-base text-ink-muted">
              Nothing else on the programme this month or next.
            </p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {upcoming.map((e) => (
                <li key={e.id} className="flex items-center gap-3.5">
                  {e.cover ? (
                    <img
                      src={e.cover.contentUrl}
                      alt=""
                      className="h-(--target-control) w-16 rounded-lg object-cover"
                    />
                  ) : (
                    <span className="h-(--target-control) w-16 shrink-0 rounded-lg border border-hairline bg-raised" />
                  )}
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate text-base font-semibold">{e.title}</span>
                    <span className="font-mono text-sm tracking-wide text-ink-muted uppercase">
                      {eventDateLabel(e.eventDate)} · {eventWhen(e)}
                    </span>
                  </div>
                  <span className="font-mono text-md text-brand-text">
                    {e.registeredSeats}/{e.capacity}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Modal
        open={!!open}
        onClose={() => setOpen(null)}
        title={open ? `${open.customer.name} · ${open.reference}` : ''}
        footer={
          <>
            {/* Collecting and cancelling belong to the screens that own the
                booking, so this one only ever shows what is there. */}
            <Button variant="dark" onClick={() => navigate('/bookings')}>
              Open in Bookings
            </Button>
            <Button variant="quiet" onClick={() => setOpen(null)}>
              Close
            </Button>
          </>
        }
      >
        {open && <BookingDetails booking={open} />}
      </Modal>

      <DrilldownModal metric={drilldown} date={today} onClose={() => setDrilldown(null)} />
    </div>
  );
}

function HourRow({
  hour,
  weekday,
  spaces,
  bookings: rows,
  onPick,
}: {
  hour: number;
  weekday: ResourceWeekday;
  spaces: AdminResource[];
  bookings: AdminBooking[];
  onPick: (b: AdminBooking) => void;
}) {
  const minute = hour * 60;
  return (
    <>
      <span className="flex h-[34px] items-center justify-end pr-2 font-mono text-md text-ink-secondary">
        {hourShort(hour)}
      </span>
      {spaces.map((s) => {
        const bk = rows.find(
          (b) =>
            b.resource.id === s.id &&
            b.startMinute !== null &&
            b.endMinute !== null &&
            minute >= b.startMinute &&
            minute < b.endMinute,
        );
        if (!bk) {
          // The rows span the widest room's window, so an hour this room is
          // shut for has to read as closed rather than as free.
          const closed = !isOpenAt(s, weekday, hour);
          return (
            <span
              key={s.id + hour}
              title={closed ? `${s.name} is closed at ${hourShort(hour)}` : undefined}
              className={
                'm-px h-[34px] rounded-md border ' +
                (closed ? 'border-transparent bg-raised' : 'border-dashed border-hairline')
              }
            />
          );
        }
        const first = bk.startMinute === minute;
        return (
          <button
            key={s.id + hour}
            onClick={() => onPick(bk)}
            title={`${bk.customer.name} · ${s.name} · ${clockRange(
              bk.startMinute ?? 0,
              bk.endMinute ?? 0,
            )}`}
            className={
              'm-px flex h-[34px] items-center justify-start overflow-hidden rounded-md border px-2 text-left text-md font-semibold transition-transform duration-(--duration-fast) ease-standard hover:-translate-y-0.5 focus-visible:outline-(length:--focus-ring-width) focus-visible:-outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color) ' +
              bookingStatusTone(bk.status)
            }
          >
            {first ? bk.customer.name : ''}
          </button>
        );
      })}
    </>
  );
}
